import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

/**
 * CustomEventLegend - Compact on-chart legend showing active custom events
 * with color indicators and toggle capability.
 * 
 * Defaults to minimised pill state showing event count and sentiment colour.
 * Click to expand full popover, click again or click away to collapse.
 */
const CustomEventLegend = ({ 
  events, 
  activeSelections, 
  onToggleEvent,
  getEventColor 
}) => {
  const containerRef = useRef(null);
  
  // State for minimised/expanded - default to minimised
  const [isExpanded, setIsExpanded] = useState(() => {
    // Check localStorage for persisted state
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('dwlp-active-events-expanded');
      if (stored !== null) {
        return stored === 'true';
      }
    }
    return false; // Default to minimised
  });

  // Determine sentiment colour for the pill indicator
  // Note: activeEvents is passed as parameter to avoid recomputing filtering logic
  const getSentimentColor = useCallback((activeEvents) => {
    if (!activeEvents || activeEvents.length === 0) return 'var(--dwlf-ink-62)';
    
    let hasBullish = false;
    let hasBearish = false;
    
    activeEvents.forEach((event) => {
      const color = getEventColor ? getEventColor(event) : '#3b82f6';
      // Guard against null/undefined color
      if (!color) return;
      // Green shades indicate bullish (exact matches only)
      if (color === '#22c55e' || color === '#4caf50') {
        hasBullish = true;
      }
      // Red shades indicate bearish (exact matches only)
      if (color === '#ef4444' || color === '#f44336') {
        hasBearish = true;
      }
    });
    
    if (hasBullish && hasBearish) {
      return '#a855f7'; // Purple for mixed
    }
    if (hasBullish) {
      return '#22c55e'; // Green for bullish
    }
    if (hasBearish) {
      return '#ef4444'; // Red for bearish
    }
    return '#3b82f6'; // Default blue for neutral
  }, [getEventColor]);

  // Toggle handler
  const handleToggle = useCallback((e) => {
    e.stopPropagation();
    setIsExpanded(prev => !prev);
  }, []);
  
  // Persist expanded state to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('dwlp-active-events-expanded', String(isExpanded));
    }
  }, [isExpanded]);

  // Handle click outside to collapse
  useEffect(() => {
    if (!isExpanded) return;
    
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsExpanded(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded]);

  // Inject fade-in animation styles with cleanup
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-4px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Compute active events after hooks
  if (!events || events.length === 0) {
    return null;
  }

  const activeEvents = events.filter((event) => {
    const id = event?.id ?? event?.eventId ?? event?.customEventId;
    return id && activeSelections.has(id);
  });

  if (activeEvents.length === 0) {
    return null;
  }

  const sentimentColor = getSentimentColor(activeEvents);

  // Minimised pill state
  if (!isExpanded) {
    return (
      <div
        ref={containerRef}
        onClick={handleToggle}
        style={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 5,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 10px',
          backgroundColor: 'var(--dwlf-elevated)',
          border: '1px solid var(--dwlf-ink-15)',
          borderRadius: 16,
          cursor: 'pointer',
          fontSize: 11,
          color: 'var(--dwlf-ink-88)',
          boxShadow: '0 2px 8px rgb(0 0 0 / 0.15)',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'var(--dwlf-accent)';
          e.currentTarget.style.transform = 'scale(1.02)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'var(--dwlf-ink-15)';
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        {/* Sentiment colour dot */}
        <span
          style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: sentimentColor,
            border: '1px solid var(--dwlf-ink-40)',
            flexShrink: 0,
          }}
        />
        <span style={{ fontWeight: 500 }}>
          {activeEvents.length} event{activeEvents.length !== 1 ? 's' : ''}
        </span>
      </div>
    );
  }

  // Expanded popover state
  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        top: 12,
        right: 12,
        zIndex: 5,
        backgroundColor: 'var(--dwlf-elevated)',
        border: '1px solid var(--dwlf-ink-15)',
        borderRadius: 6,
        padding: '8px 10px',
        maxWidth: 220,
        boxShadow: '0 4px 12px rgb(0 0 0 / 0.2)',
        animation: 'fadeIn 0.15s ease',
      }}
    >
      {/* Header with collapse button */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 6,
          cursor: 'pointer',
        }}
        onClick={handleToggle}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: 'var(--dwlf-ink-62)',
          }}
        >
          Active Events
        </div>
        <div
          style={{
            fontSize: 10,
            color: 'var(--dwlf-ink-62)',
            padding: '2px 4px',
            borderRadius: 3,
            transition: 'background-color 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--dwlf-ink-10)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          ✕
        </div>
      </div>
      {activeEvents.map((event) => {
        const id = event?.id ?? event?.eventId ?? event?.customEventId;
        const name = event?.name ?? event?.eventName ?? 'Event';
        const color = getEventColor ? getEventColor(event) : '#3b82f6';

        return (
          <div
            key={id}
            onClick={(e) => {
              e.stopPropagation();
              onToggleEvent?.(id);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 6px',
              marginBottom: 3,
              cursor: 'pointer',
              borderRadius: 4,
              fontSize: 11,
              color: 'var(--dwlf-ink-88)',
              backgroundColor: 'transparent',
              transition: 'background-color 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--dwlf-ink-08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {/* Color dot */}
            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: color,
                border: '1px solid var(--dwlf-ink-40)',
                flexShrink: 0,
              }}
            />
            {/* Event name */}
            <span
              style={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {name}
            </span>
            {/* Eye icon to indicate visibility */}
            <span
              style={{
                fontSize: 9,
                color: 'var(--dwlf-ink-62)',
                flexShrink: 0,
              }}
            >
              👁
            </span>
          </div>
        );
      })}
    </div>
  );
};

CustomEventLegend.propTypes = {
  events: PropTypes.arrayOf(PropTypes.object),
  activeSelections: PropTypes.instanceOf(Set),
  onToggleEvent: PropTypes.func,
  getEventColor: PropTypes.func,
};

CustomEventLegend.defaultProps = {
  events: [],
  activeSelections: new Set(),
  onToggleEvent: null,
  getEventColor: null,
};

export default CustomEventLegend;
